import React, { useEffect, useState } from "react";
import { X, Loader2, Mail, MessageCircle, FileText, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full px-2.5 py-1.5 text-sm border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary outline-none";

function openPdf(dossierId, kind) {
  const win = window.open("", "_blank");
  fetch(`${API}/dossiers/${dossierId}/envoi-pose/pdf/${kind}`, { headers: { Authorization: `Bearer ${localStorage.getItem("norea_token")}` } })
    .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
    .then((b) => {
      const url = URL.createObjectURL(b);
      if (win) win.document.body.innerHTML = `<iframe src="${url}" style="border:0;width:100vw;height:100vh;position:fixed;inset:0"></iframe>`;
    })
    .catch(() => { if (win) win.close(); toast.error("Génération du PDF impossible"); });
}

function EmailSection({ dossierId, draft, onSent }) {
  const [form, setForm] = useState({ ...draft.email, cc: (draft.email.cc || []).join(", ") });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const send = async () => {
    setSending(true);
    try {
      await api.post(`/dossiers/${dossierId}/envoi-pose/send-email`, { ...form, cc: form.cc.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Email envoyé au sous-traitant");
      setSent(true);
      onSent("email");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };
  return (
    <section className="space-y-2" data-testid="envoi-pose-email">
      <h4 className="text-sm font-semibold flex items-center gap-1.5"><Mail className="w-4 h-4 text-norea-primary" /> Email au sous-traitant {draft.sous_traitant && <span className="font-normal text-norea-textMuted">· {draft.sous_traitant}</span>}</h4>
      <input value={form.to} onChange={set("to")} placeholder="Email du sous-traitant" className={inputCls} data-testid="envoi-pose-to" />
      <input value={form.cc} onChange={set("cc")} placeholder="Copie (séparées par des virgules)" className={inputCls} data-testid="envoi-pose-cc" />
      <input value={form.subject} onChange={set("subject")} className={inputCls} data-testid="envoi-pose-subject" />
      <textarea rows={7} value={form.body} onChange={set("body")} className={inputCls} data-testid="envoi-pose-body" />
      <div className="flex flex-wrap items-center gap-2">
        {draft.documents.map((d) => (
          <button key={d.kind} type="button" onClick={() => openPdf(dossierId, d.kind)} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-norea-border text-[11px] hover:bg-norea-surfaceHover" data-testid={`envoi-pose-doc-${d.kind}`}>
            <FileText className="w-3.5 h-3.5 text-norea-primary" /> {d.label}
          </button>
        ))}
        <span className="text-[11px] text-norea-textMuted">joints en PDF</span>
        <button onClick={send} disabled={sending || sent || !form.to} className="btn-primary text-sm ml-auto" data-testid="envoi-pose-send-email">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : sent ? <CheckCircle2 className="w-4 h-4" /> : <Send className="w-4 h-4" />} {sent ? "Envoyé" : "Envoyer l'email"}
        </button>
      </div>
    </section>
  );
}

function WhatsAppSection({ dossierId, draft, onSent }) {
  const [to, setTo] = useState(draft.whatsapp.to || "");
  const [text, setText] = useState(draft.whatsapp.text);
  const [opened, setOpened] = useState(false);
  const [logged, setLogged] = useState(false);
  const open = () => {
    window.open(`https://wa.me/${to.replace(/\D/g, "")}?text=${encodeURIComponent(text)}`, "_blank");
    setOpened(true);
  };
  const confirm = async () => {
    try {
      await api.post(`/dossiers/${dossierId}/envoi-pose/whatsapp-sent`, { to });
      setLogged(true);
      onSent("whatsapp");
      toast.success("Envoi WhatsApp enregistré");
    } catch { toast.error("Enregistrement impossible"); }
  };
  return (
    <section className="space-y-2" data-testid="envoi-pose-whatsapp">
      <h4 className="text-sm font-semibold flex items-center gap-1.5"><MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp à la régie {draft.whatsapp.name && <span className="font-normal text-norea-textMuted">· {draft.whatsapp.name}</span>}</h4>
      <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Numéro WhatsApp (ex. 33612345678)" className={inputCls} data-testid="envoi-pose-wa-to" />
      <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} className={inputCls} data-testid="envoi-pose-wa-text" />
      <div className="flex justify-end items-center gap-2">
        {opened && !logged && <button onClick={confirm} className="btn-primary text-sm" data-testid="envoi-pose-wa-confirm"><CheckCircle2 className="w-4 h-4" /> J'ai envoyé le message</button>}
        {logged && <span className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="envoi-pose-wa-logged"><CheckCircle2 className="w-3.5 h-3.5" /> Envoi WhatsApp enregistré</span>}
        <button onClick={open} disabled={!to} className="btn-secondary text-sm" data-testid="envoi-pose-open-wa"><MessageCircle className="w-4 h-4 text-emerald-600" /> Ouvrir WhatsApp</button>
      </div>
    </section>
  );
}

export default function EnvoiPoseModal({ dossierId, onClose, onSent }) {
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    api.get(`/dossiers/${dossierId}/envoi-pose/draft`, { params: { base_url: BASE } }).then((r) => setDraft(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Préparation impossible"); onClose(); });
  }, [dossierId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl border border-norea-border" onClick={(e) => e.stopPropagation()} data-testid="envoi-pose-modal">
        <div className="px-6 py-4 border-b border-norea-border flex items-center justify-between">
          <h3 className="font-heading text-xl font-medium">Envoi en pose</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-norea-surfaceHover" data-testid="envoi-pose-close"><X className="w-4 h-4" /></button>
        </div>
        {!draft ? <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
          <div className="p-6 space-y-6">
            <p className="text-xs text-norea-textMuted">Vérifiez et modifiez les messages avant l'envoi. Les PDF sont générés à partir des champs du dossier (onglet Installation).</p>
            <EmailSection dossierId={dossierId} draft={draft} onSent={onSent} />
            <div className="border-t border-norea-border" />
            <WhatsAppSection dossierId={dossierId} draft={draft} onSent={onSent} />
          </div>
        )}
      </div>
    </div>
  );
}
